import React, { useRef, useState } from "react";
import styles from "./NewPostSocialFeedForModal.module.scss";
import AutoGrowingTextarea from "../UI/AutoGrowingTextArea/AutoGrowingTextArea";
import ImageSVG from "../SVGComponents/ImageSVG";
import VideoSVG from "../SVGComponents/VideoSVG";
import SendSVG from "../SVGComponents/SendSVG";
import { RxCross2 } from "react-icons/rx";
import Modal from "../Modal/Modal";
import { API } from "../../service/Interceptor";
import { combineClasses, serverFiles, compressImage } from "../../utils/utils";
import toast from "../Toast/toast";
import { useDispatch } from "react-redux";
import { createPost, getAllPosts } from "../../reducer/postsSlice";
import SpinnerLoader from "../UI/SpinnerLoader/SpinnerLoader";
import Tooltip from "components/common/Tooltip/Tooltip";
import CrossIconSVG from "components/SVGComponents/CrossIconSVG";
import { useDisclosure } from "hooks";
import { ReactComponent as AddImageIcon } from "../../../src/images/SVG/AddPostIcon.svg";

const tagsOptions = [
  // {
  //   label: "All",
  //   value: "ALL",
  // },
  {
    label: "Advice",
    value: "ADVICE",
  },
  {
    label: "Network",
    value: "NETWORK",
  },
  {
    label: "Thoughts",
    value: "THOUGHTS",
  },
];

const NewPostSocialFeedForModal = ({ containerClassName, onClose }) => {
  const fileInputRef = useRef(null);

  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const {
    isOpen: tagsModal,
    close: closeTagsModal,
    toggle: togglTagsModale,
  } = useDisclosure();

  const [mediaIds, setMediaIds] = useState([]);
  const dispatch = useDispatch();

  const unixTimestamp = Math.floor(Date.now() / 1000);

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files || []);

    // const isAcceptedType = (file) =>
    //   type === "image"
    //     ? file.type.startsWith("image/")
    //     : file.type.startsWith("video/");

    // const rejectedFiles = files.filter((file) => !isAcceptedType(file));

    // if (rejectedFiles?.length > 0) {
    //   toast.error(`Only ${type} files are allowed. Some files were rejected.`);
    //   return;
    // }

    const MAX_VIDEO_SIZE = 20 * 1024 * 1024; // 20MB

    for (let file of files) {
      let fileToUpload = file;

      const mediaType = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : null;

      if (mediaType === "image") {
        fileToUpload = await compressImage(file);
      }

      const previewUrl = URL.createObjectURL(fileToUpload);
      const tempId = Math.random().toString(36).substr(2, 9);

      const formData = new FormData();
      formData.append("fileName", fileToUpload);

      if (mediaType === "image") {
        // Add loading preview
        setImages((prev) => [
          ...prev,
          { id: tempId, previewUrl, uploading: true },
        ]);

        try {
          const res = await API.post("api/media", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          const uploadedFile = res.data[0];

          setImages((prev) =>
            prev.map((img) =>
              img.id === tempId
                ? {
                  id: uploadedFile.id,
                  path: uploadedFile.path,
                  previewUrl: null,
                  uploading: false,
                }
                : img
            )
          );
          setMediaIds((prev) => [...prev, uploadedFile.id]);
        } catch (err) {
          console.error("Image upload failed:", err);
        }
      }

      if (mediaType === "video") {
        if (file.size > MAX_VIDEO_SIZE) {
          toast.error(`"${file.name}" exceeds 20MB limit and was skipped.`);
          continue;
        }

        // Add loading preview
        setVideos((prev) => [
          ...prev,
          { id: tempId, previewUrl, uploading: true },
        ]);

        try {
          const res = await API.post("api/media", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          const uploadedFile = res.data[0];

          setVideos((prev) =>
            prev.map((vid) =>
              vid.id === tempId
                ? {
                  id: uploadedFile.id,
                  path: uploadedFile.path,
                  previewUrl: null,
                  uploading: false,
                }
                : vid
            )
          );
          setMediaIds((prev) => [...prev, uploadedFile.id]);
        } catch (err) {
          console.error("Video upload failed:", err);
        }
      }
    }
  };

  const validateDescription = (val) => {
    if (!val.trim()) return "Description cannot be empty.";
    if (val.length < 10) return "Minimum 10 characters required.";
    if (val.length > 5000) return "Maximum 5000 characters allowed.";
    return "";
  };

  const handlePost = async () => {
    if (!selectedTag) {
      toast.error("Please select a tag and enter a description");
      return;
    }

    if (
      description === "" &&
      selectedTag === "" &&
      images.length === 0 &&
      videos.length === 0
    ) {
      toast.error("Cannot Create Post");
      return;
    }

    const descriptionError = validateDescription(description);
    if (descriptionError) {
      toast.error(descriptionError);
      return;
    }

    const payload = {
      tags: [selectedTag],
      content: description,
      mediaIds: mediaIds,
    };

    try {
      setLoading(true);
      const res = await dispatch(createPost(payload));
      // console.log(res);

      if (res?.payload) {
        toast.success("Uploaded Successfully");
        setDescription("");
        setSelectedTag("");
        setImages([]);
        setVideos([]);
        setMediaIds([]);
        dispatch(getAllPosts({ pageNo: 1, perPage: 5, sortBy: "latest" }));
        onClose();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => {
      const removedImage = prevImages[index];
      setMediaIds((prevIds) => prevIds.filter((id) => id !== removedImage.id));
      return prevImages.filter((_, i) => i !== index);
    });
  };

  const handleRemoveVideo = (index) => {
    setVideos((prevVideos) => {
      const removedVideo = prevVideos[index];
      setMediaIds((prevIds) => prevIds.filter((id) => id !== removedVideo.id));
      return prevVideos.filter((_, i) => i !== index);
    });
  };

  return (
    <>
      <section
        className={combineClasses(styles.newPostSocialFeed, containerClassName)}
      >
        <div className={styles.topHeading}>
          <p>Post</p>
        </div>
        <div className={styles.newPostSeperator} />
        {selectedTag !== "" && (
          <div
            onClick={() => setSelectedTag("")}
            className={styles.selected_tag}
          >
            <p>{selectedTag}</p>
            <CrossIconSVG color="white" height="16" width="16" />
          </div>
        )}
        <p className={styles.containerHeading}>What is this post about?</p>
        <div className={styles.tagsSection}>
          <div className={styles.tagsGrid}>
            {tagsOptions?.map((option, index) => (
              <button
                key={index}
                className={`${styles.tagButton} ${selectedTag === option.label ? styles.selected : ""
                  }`}
                onClick={() => {
                  closeTagsModal();
                  setSelectedTag(option.label);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <p className={styles.containerHeading}>Your Post</p>
        <div className={styles.newPostInput}>
          <AutoGrowingTextarea
            value={description}
            placeholder="What would you like to share?"
            onChange={(e) => setDescription(e.target.value)}
            className={styles.newPost_textarea}
            rows={6}
          />
          <input
            ref={fileInputRef}
            style={{ display: "none" }}
            id={`image-upload-${unixTimestamp}`}
            type="file"
            accept="image/*,video/*"
            multiple
            placeholder=""
            onChange={handleMediaUpload}
          />
          {images.length > 0 || videos.length > 0 ? (
            <div className={styles.image_preview_container}>
              <div className={styles.preview_Media}>
                {images?.map((img, idx) => (
                  <div
                    key={`imgL{idx}`}
                    className={styles.preview_imageWrapper}
                  >
                    <img
                      src={img.previewUrl || serverFiles(img.path)}
                      alt={`upload-img-${idx}`}
                      style={{ opacity: img.uploading ? 0.5 : 1 }}
                    />
                    {img.uploading && (
                      <div className={styles.loadingOverlay}>
                        <SpinnerLoader />
                      </div>
                    )}
                    {!img.uploading && (
                      <button
                        className={styles.preview_removeBtn}
                        onClick={() => handleRemoveImage(idx)}
                      >
                        <RxCross2 color="white" />
                      </button>
                    )}
                  </div>
                ))}

                {videos?.map((vid, idx) => (
                  <div
                    key={`vid-${idx}`}
                    className={styles.preview_imageWrapper}
                  >
                    <video
                      src={vid.previewUrl || serverFiles(vid.path)}
                      controls
                      width="350"
                      height="350"
                      style={{ opacity: vid.uploading ? 0.5 : 1 }}
                    />
                    {vid.uploading && (
                      <div className={styles.loadingOverlay}>
                        <SpinnerLoader />
                      </div>
                    )}
                    {!vid.uploading && (
                      <button
                        className={styles.preview_removeBtn}
                        onClick={() => handleRemoveVideo(idx)}
                      >
                        <RxCross2 color="#6570FC" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                className={styles.plusiconpreview}
                onClick={() => fileInputRef.current?.click()}
              >
                <AddImageIcon />
              </button>
            </div>
          ) : (
            <div
              className={styles.image_preview_container_empty}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className={styles.imgLabelContainer}>
                <ImageSVG
                  style={{ width: "30px", height: "30px" }}
                  color="#C8CDF3"
                />

                <p className={styles.imgLabel}>Add Image</p>
              </div>
            </div>
          )}

          <button className={styles.post_button} data-loading={loading} disabled={loading} onClick={handlePost}>
            {loading ? <SpinnerLoader containerClassName={styles.post_button_loader_container} loaderClassName={styles.post_button_loader} loaderSize={20} /> : null}
            {loading ? "Posting..." : "Post"}
          </button>
        </div>
      </section>
    </>
  );
};

export default NewPostSocialFeedForModal;
